import type {
  AuthorInput, CategoryInput, CategoryNode, CreateProductInput, MovementFilter, MovementListItem,
  Paginated, ProductFilter, ProductInput, ProductListItem, ProductType, PublisherInput,
  StockAdjustmentInput, StockCountInput,
} from '@najah/core'
import { api } from './api'

/* ── التصنيفات ── */
export interface CategoryFlat {
  id: string
  nameAr: string
  parentId: string | null
  isActive: boolean
  icon: string | null
  sortOrder: number
}

export const categoriesApi = {
  list: () => api<CategoryFlat[]>('/categories'),
  tree: (includeInactive = false) =>
    api<CategoryNode[]>('/categories/tree', { query: { includeInactive: String(includeInactive) } }),
  create: (body: CategoryInput) => api<{ id: string }>('/categories', { method: 'POST', body }),
  update: (id: string, body: Partial<CategoryInput>) =>
    api<{ id: string }>(`/categories/${id}`, { method: 'PATCH', body }),
  remove: (id: string) => api<{ success: boolean }>(`/categories/${id}`, { method: 'DELETE' }),
}

/* ── المنتجات ── */
export interface ProductDetail extends Omit<ProductListItem, 'createdAt'> {
  description: string | null
  wholesalePrice: number | null
  createdAt: string
  category: { id: string; nameAr: string } | null
  bookDetail: {
    isbn: string | null
    authorId: string | null
    publisherId: string | null
    pages: number | null
    language: string | null
    edition: string | null
    publishYear: number | null
    author: { id: string; nameAr: string } | null
    publisher: { id: string; nameAr: string } | null
  } | null
}

export const productsApi = {
  list: (filter: Partial<ProductFilter>) =>
    api<Paginated<ProductListItem>>('/products', { query: filter as Record<string, string | number | undefined> }),
  get: (id: string) => api<ProductDetail>(`/products/${id}`),
  byBarcode: (code: string) => api<ProductDetail>(`/products/barcode/${encodeURIComponent(code)}`),
  suggestSku: (type: ProductType) => api<{ sku: string }>('/products/suggest-sku', { query: { type } }),
  create: (body: CreateProductInput) => api<ProductDetail>('/products', { method: 'POST', body }),
  update: (id: string, body: ProductInput) => api<ProductDetail>(`/products/${id}`, { method: 'PATCH', body }),
  remove: (id: string) => api<{ success: boolean }>(`/products/${id}`, { method: 'DELETE' }),
}

/* ── المخزون ── */
export interface InventorySummary {
  storeId: string
  totalProducts: number
  outOfStock: number
  lowStock: number
  totalUnits: number
  stockValue: number
}

export interface IntegrityReport {
  checked: number
  drift: { productId: string; ledger: number; cached: number }[]
  healthy: boolean
}

export const inventoryApi = {
  summary: () => api<InventorySummary>('/inventory/summary'),
  movements: (filter: Partial<MovementFilter>) =>
    api<Paginated<MovementListItem>>('/inventory/movements', {
      query: filter as Record<string, string | number | undefined>,
    }),
  adjust: (body: StockAdjustmentInput) =>
    api<{ movementId: string; productName: string; quantity: number }>('/inventory/adjust', { method: 'POST', body }),
  count: (body: StockCountInput) =>
    api<{ movementId: string | null; quantity: number; difference: number }>('/inventory/count', {
      method: 'POST',
      body,
    }),
  integrity: () => api<IntegrityReport>('/inventory/integrity'),
}

/* ── المؤلّفون والناشرون ── */
export interface Author { id: string; nameAr: string; nameFr: string | null }
export interface Publisher { id: string; nameAr: string; nameFr: string | null; country: string | null }

export const lookupsApi = {
  authors: () => api<Author[]>('/lookups/authors'),
  createAuthor: (body: AuthorInput) => api<Author>('/lookups/authors', { method: 'POST', body }),
  publishers: () => api<Publisher[]>('/lookups/publishers'),
  createPublisher: (body: PublisherInput) => api<Publisher>('/lookups/publishers', { method: 'POST', body }),
}

/** يسطّح شجرة التصنيفات إلى خيارات قائمة مع بادئة تُظهر العمق */
export function flattenCategories(nodes: CategoryNode[], depth = 0): { value: string; label: string }[] {
  return nodes.flatMap((n) => [
    { value: n.id, label: `${'— '.repeat(depth)}${n.nameAr}` },
    ...flattenCategories(n.children, depth + 1),
  ])
}
